import {
  For,
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { A } from '@solidjs/router';
import { t } from '@/shared/i18n';
import { usePublicPath } from '@/shared/lib/usePublicPath';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import { IsometricBg } from '@/shared/components/IsometricBg';
import MarketingNavbar from './components/MarketingNavbar';
import './LandingPage.css';

const stepKeys = ['step1', 'step2', 'step3'] as const;
const faqKeys = ['1', '2', '3', '4', '5'] as const;

// ── Interactive hero flow diagram ────────────────────────────────────────────
function HeroFlowDiagram() {
  const [phase, setPhase] = createSignal(0);
  // 0=idle  1=wire1 drawing  2=table lit  3=wires 2+3 drawing  4=profit lit (final — no reset)

  // DOM refs for pixel-perfect wire calculation
  let containerRef!: HTMLDivElement;
  let importRef!: HTMLButtonElement;
  let tableRef!: HTMLDivElement;
  let sellRef!: HTMLButtonElement;
  let revenueRef!: HTMLDivElement;

  // Computed SVG path strings
  const [paths, setPaths] = createSignal({ w1: '', w2: '', w3: '' });

  function calcWires() {
    if (!containerRef || !importRef || !tableRef || !sellRef || !revenueRef)
      return;
    const cr = containerRef.getBoundingClientRect();
    const loc = (el: Element) => {
      const r = el.getBoundingClientRect();
      return {
        left: r.left - cr.left,
        right: r.right - cr.left,
        top: r.top - cr.top,
        bottom: r.bottom - cr.top,
        cx: (r.left + r.right) / 2 - cr.left,
        cy: (r.top + r.bottom) / 2 - cr.top,
      };
    };
    const imp = loc(importRef);
    const tbl = loc(tableRef);
    const sel = loc(sellRef);
    const rev = loc(revenueRef);
    // W1: Import right-center → Table left at import center-y (horizontal)
    const w1 = `M ${imp.right} ${imp.cy} L ${tbl.left} ${imp.cy}`;
    // W2: Table center-bottom → drop to sell center-y → Sell right-center (L-shape)
    const w2 = `M ${tbl.cx} ${tbl.bottom} L ${tbl.cx} ${sel.cy} L ${sel.right} ${sel.cy}`;
    // W3: Sell bottom-center → Revenue top-center (straight vertical)
    const w3 = `M ${sel.cx} ${sel.bottom} L ${sel.cx} ${rev.top}`;
    setPaths({ w1, w2, w3 });
  }

  onMount(() => {
    calcWires();
    // Use rAF so the read happens after paint, not synchronously mid-layout
    const onResize = () => requestAnimationFrame(calcWires);
    window.addEventListener('resize', onResize);
    onCleanup(() => window.removeEventListener('resize', onResize));
  });

  // Recalculate after each phase change (table rows appear/disappear, changing table height)
  // rAF defers the getBoundingClientRect reads until after the browser has
  // finished applying the new DOM state, preventing a forced reflow.
  createEffect(() => {
    phase();
    requestAnimationFrame(calcWires);
  });

  const handleImport = () => {
    if (phase() !== 0) return;
    setPhase(1);
    setTimeout(() => {
      setPhase(2);
      setTimeout(() => setPhase(3), 750); // wait for W2 animation to finish before unlocking Sell
    }, 680);
  };

  const handleSell = () => {
    if (phase() !== 3) return;
    setPhase(4);
    setTimeout(() => setPhase(5), 820);
  };

  // wire state helpers
  const w1 = () => (phase() >= 2 ? 'done' : phase() === 1 ? 'drawing' : 'idle');
  const w2 = () => (phase() >= 3 ? 'done' : phase() === 2 ? 'drawing' : 'idle');
  const w3 = () => (phase() >= 5 ? 'done' : phase() === 4 ? 'drawing' : 'idle');

  const anim = (s: string, dur: string, delay = '0ms') =>
    s === 'drawing' ? `draw-wire ${dur} ${delay} ease-out forwards` : 'none';

  return (
    <div class="from-white/26 to-white/8 rounded-[24px] border border-white/35 bg-gradient-to-b p-5 shadow-[0_22px_58px_rgba(120,53,15,0.35)] backdrop-blur-md">
      {/* Window chrome */}
      <div class="flex items-center justify-between rounded-xl border border-white/30 bg-white/15 px-4 py-2.5">
        <div class="flex items-center gap-1.5">
          <span class="h-2.5 w-2.5 rounded-full bg-red-300/90" />
          <span class="h-2.5 w-2.5 rounded-full bg-amber-200/90" />
          <span class="h-2.5 w-2.5 rounded-full bg-green-300/90" />
        </div>
        <span class="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">
          {t('landing.hero.flow.label')}
        </span>
        <Show when={phase() >= 5}>
          <span class="rounded-full bg-[rgba(74,222,128,0.14)] px-2.5 py-0.5 text-[10px] font-bold text-[rgba(74,222,128,0.7)]">
            {t('landing.hero.flow.done')}
          </span>
        </Show>
        <Show when={phase() < 5}>
          <span class="w-[52px]" />
        </Show>
      </div>

      {/* Canvas — nodes laid out in a flex column; SVG overlay draws wires between measured positions */}
      <div ref={containerRef} class="relative mt-3 flex flex-col gap-8 pb-5">
        {/* ── SVG wire layer — no viewBox, pixel coords from getBoundingClientRect ── */}
        <svg class="pointer-events-none absolute inset-0 z-[-1] h-full w-full overflow-visible">
          {/* Gray track — always visible */}
          <path
            d={paths().w1}
            stroke="rgb(148,163,184)"
            stroke-width="1.5"
            stroke-linecap="round"
            fill="none"
          />
          <path
            d={paths().w2}
            stroke="rgb(148,163,184)"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
          />
          <path
            d={paths().w3}
            stroke="rgb(148,163,184)"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
          />
          {/* Green animated overlay */}
          <path
            d={paths().w1}
            pathLength="1"
            stroke-dasharray={w1() === 'drawing' ? '1' : 'none'}
            stroke="rgb(34,197,94)"
            stroke-width={w1() === 'idle' ? '0' : '2'}
            stroke-linecap="round"
            fill="none"
            style={{ animation: anim(w1(), '0.5s') }}
          />
          <path
            d={paths().w2}
            pathLength="1"
            stroke-dasharray={w2() === 'drawing' ? '1' : 'none'}
            stroke="rgb(34,197,94)"
            stroke-width={w2() === 'idle' ? '0' : '2'}
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
            style={{ animation: anim(w2(), '0.65s') }}
          />
          <path
            d={paths().w3}
            pathLength="1"
            stroke-dasharray={w3() === 'drawing' ? '1' : 'none'}
            stroke="rgb(34,197,94)"
            stroke-width={w3() === 'idle' ? '0' : '2'}
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
            style={{ animation: anim(w3(), '0.45s') }}
          />
        </svg>

        {/* ── Row 1: Import (left) + Table (right, floated right) ── */}
        <div class="flex items-center justify-between gap-3">
          {/* Node 1: Import Data */}
          <div class="flex w-[28%] shrink-0 items-center">
            <button
              ref={importRef}
              onClick={handleImport}
              disabled={phase() !== 0}
              class={`w-full rounded-lg border px-3 py-2 text-xs font-bold transition-colors duration-300 ${
                phase() === 0
                  ? 'import-hint cursor-pointer border-slate-300 bg-white text-slate-700'
                  : 'cursor-default border-[rgba(74,222,128,0.7)] bg-white text-slate-600'
              }`}
            >
              {t('landing.hero.flow.importBtn')}
              <Show when={phase() >= 1}>
                <span class="ml-1.5 text-[rgba(34,197,94,0.9)]">✓</span>
              </Show>
            </button>
          </div>
          {/* Node 2: Product table — floated right, fixed 55% width */}
          <div
            ref={tableRef}
            class={`w-[55%] overflow-hidden rounded-lg border transition-colors duration-500 ${
              phase() >= 2
                ? 'border-[rgba(74,222,128,0.55)] bg-white'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div class="grid grid-cols-[1.4fr_0.55fr_1fr] gap-px border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-600">
              <div class="px-2 py-1.5">
                {t('landing.hero.flow.tableProduct')}
              </div>
              <div class="px-2 py-1.5 text-right">
                {t('landing.hero.flow.tableQty')}
              </div>
              <div class="px-2 py-1.5 text-right">
                {t('landing.hero.flow.tablePrice')}
              </div>
            </div>
            <For
              each={[
                {
                  sku: t('landing.hero.flow.row1Sku'),
                  qty: 200,
                  price: t('landing.hero.flow.row1Price'),
                },
                {
                  sku: t('landing.hero.flow.row2Sku'),
                  qty: 80,
                  price: t('landing.hero.flow.row2Price'),
                },
                {
                  sku: t('landing.hero.flow.row3Sku'),
                  qty: 500,
                  price: t('landing.hero.flow.row3Price'),
                },
              ]}
            >
              {(row, i) => (
                <div
                  class="grid grid-cols-[1.4fr_0.55fr_1fr] gap-px border-t border-slate-100 text-[11px] transition-colors duration-500"
                  style={{
                    animation:
                      phase() >= 2
                        ? `hero-fade-up 0.26s ease both ${i() * 70}ms`
                        : 'none',
                  }}
                >
                  <div
                    class={`px-2 py-1.5 font-medium transition-colors duration-500 ${phase() >= 2 ? 'text-slate-700' : 'text-slate-600'}`}
                  >
                    {row.sku}
                  </div>
                  <div
                    class={`px-2 py-1.5 text-right transition-colors duration-500 ${phase() >= 2 ? 'text-slate-500' : 'text-slate-600'}`}
                  >
                    {row.qty}
                  </div>
                  <div
                    class={`px-2 py-1.5 text-right font-semibold transition-colors duration-500 ${phase() >= 2 ? 'text-amber-600' : 'text-slate-600'}`}
                  >
                    {row.price}
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>

        {/* ── Row 2: Sell (left, aligned with Import column) ── */}
        <div class="flex w-[28%] shrink-0">
          <button
            ref={sellRef}
            onClick={handleSell}
            disabled={phase() !== 3}
            class={`w-full rounded-lg border px-3 py-2 text-xs font-bold transition-colors duration-300 ${
              phase() === 3
                ? 'flow-hint cursor-pointer border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'
                : phase() >= 4
                  ? 'cursor-default border-[rgba(74,222,128,0.7)] bg-white text-slate-600'
                  : 'cursor-default border-slate-200 bg-white text-slate-500'
            }`}
          >
            {t('landing.hero.flow.sellBtn')}
            <Show when={phase() >= 4}>
              <span class="ml-1.5 text-[rgba(74,222,128,0.85)]">✓</span>
            </Show>
          </button>
        </div>

        {/* ── Row 3: Revenue (full width) ── */}
        <div
          ref={revenueRef}
          class={`rounded-xl border bg-white px-4 py-3 transition-colors duration-500 ${
            phase() >= 5 ? 'border-[rgba(74,222,128,0.7)]' : 'border-slate-200'
          }`}
        >
          <div class="flex items-center justify-between">
            <span
              class={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                phase() >= 5 ? 'text-slate-600' : 'text-slate-500'
              }`}
            >
              {t('landing.hero.flow.revenueLabel')}
            </span>
            <span
              class={`text-xl font-black transition-colors duration-700 ${
                phase() >= 5 ? 'text-amber-600' : 'text-slate-400'
              }`}
            >
              {phase() >= 5
                ? t('landing.hero.flow.revenueAmount')
                : t('landing.hero.flow.revenueEmpty')}
            </span>
          </div>
          <Show when={phase() >= 5}>
            <p class="mt-1 text-[11px] text-slate-400">
              {t('landing.hero.flow.revenueDetail')}
            </p>
          </Show>
        </div>
      </div>
    </div>
  );
}

const problemItems = ['item1', 'item2', 'item3', 'item4'] as const;

export default function LandingPage() {
  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'khoai.io',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          url: 'https://khoai.io',
          description:
            'Real-time inventory and store management across multiple warehouses and stores. Track every SKU, prevent stockouts, scan barcodes, run audits, and manage stock transfers from any device.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: {
            '@type': 'Organization',
            name: 'khoai.io',
            url: 'https://khoai.io',
          },
        },
        {
          '@type': 'Organization',
          '@id': 'https://khoai.io/#organization',
          name: 'khoai.io',
          url: 'https://khoai.io',
          logo: {
            '@type': 'ImageObject',
            url: 'https://khoai.io/og-welcome.jpg',
          },
          sameAs: ['https://www.facebook.com/share/1Bbe3AzKGf/'],
        },
        {
          '@type': 'WebSite',
          '@id': 'https://khoai.io/#website',
          url: 'https://khoai.io',
          name: 'khoai.io',
          publisher: { '@id': 'https://khoai.io/#organization' },
          inLanguage: ['en', 'vi'],
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-json-landing';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    onCleanup(() => document.getElementById('ld-json-landing')?.remove());
  });

  const pp = usePublicPath();

  return (
    <div class="landing-light relative flex min-h-screen flex-col bg-white text-slate-900">
      <IsometricBg
        class="fixed inset-0 z-0 h-full w-full opacity-30"
        count={14}
        speed={0.7}
      />
      <div class="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_15%_20%,rgba(251,191,36,0.14),transparent_45%),radial-gradient(circle_at_80%_10%,rgba(120,113,108,0.08),transparent_35%)]" />

      {/* ═══════════════════ Nav ═══════════════════ */}
      <MarketingNavbar lightTheme />

      <main>
        {/* ═══════════════════ Hero ═══════════════════ */}
        <section class="relative z-10 flex min-h-[calc(100vh-57px)] flex-col justify-center py-12 sm:py-16">
          <div class="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.02fr)] lg:gap-12">
              <header class="text-center lg:text-left">
                <h1
                  class="text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
                  style={{
                    animation:
                      'hero-fade-up 0.62s cubic-bezier(0.22,1,0.36,1) both',
                  }}
                >
                  {t('landing.hero.title')}
                </h1>
                <p
                  class="mt-5 text-base font-medium leading-relaxed text-amber-50 sm:text-lg"
                  style={{
                    animation:
                      'hero-fade-up 0.62s cubic-bezier(0.22,1,0.36,1) both 90ms',
                  }}
                >
                  {t('landing.hero.subtitle').split('khoai.io')[0]}
                  <span class="font-bold text-white">khoai.io</span>
                  {t('landing.hero.subtitle').split('khoai.io')[1]}
                </p>

                <div
                  class="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start"
                  style={{
                    animation:
                      'hero-fade-up 0.62s cubic-bezier(0.22,1,0.36,1) both 160ms',
                  }}
                >
                  <A
                    href="/signup"
                    onClick={() =>
                      trackMarketingEvent('landing_cta_click', {
                        placement: 'hero_primary',
                        target: '/signup',
                      })
                    }
                    class="rounded-xl bg-amber-600 px-7 py-3 text-center text-base font-extrabold text-white shadow-[0_14px_30px_rgba(180,83,9,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-700"
                  >
                    {t('landing.hero.getStarted')}
                  </A>
                  <A
                    href={pp('/pricing')}
                    onClick={() =>
                      trackMarketingEvent('landing_cta_click', {
                        placement: 'hero_secondary',
                        target: '/pricing',
                      })
                    }
                    class="rounded-xl border border-slate-300 bg-white px-7 py-3 text-center text-base font-bold text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-50"
                  >
                    {t('landing.hero.viewPricing')}
                  </A>
                </div>

                <div
                  class="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 lg:justify-start"
                  style={{
                    animation:
                      'hero-fade-up 0.62s cubic-bezier(0.22,1,0.36,1) both 240ms',
                  }}
                >
                  <ul class="flex flex-wrap justify-center gap-x-6 gap-y-2 lg:justify-start">
                    <li class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-50">
                      <span class="text-green-300">✓</span>
                      {t('landing.hero.trust1')}
                    </li>
                    <li class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-50">
                      <span class="text-green-300">✓</span>
                      {t('landing.hero.trust2')}
                    </li>
                    <li class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-50">
                      <span class="text-green-300">✓</span>
                      {t('landing.hero.trust3')}
                    </li>
                  </ul>
                </div>
              </header>

              {/* ── Hero flow diagram ── */}
              <div
                class="relative hidden w-full lg:block"
                style={{
                  animation:
                    'hero-fade-up 0.68s cubic-bezier(0.22,1,0.36,1) both 220ms',
                }}
              >
                <HeroFlowDiagram />
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ How It Works ═══════════════════ */}
        <section class="landing-band landing-band--clear relative z-10 py-20 sm:py-24">
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="text-center">
              <h2 class="text-4xl font-extrabold text-white sm:text-5xl">
                {t('landing.howItWorks.title')}
              </h2>
              <p class="mx-auto mt-4 max-w-2xl text-lg font-medium text-white/90">
                {t('landing.howItWorks.subtitle')}
              </p>
            </div>

            <div class="mt-16 flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-0">
              <For each={stepKeys}>
                {(step, i) => (
                  <>
                    <article
                      class="landing-card landing-card--interactive flex-1 p-8"
                      style={{
                        animation: 'hero-fade-up 0.5s ease both',
                        'animation-delay': `${i() * 90}ms`,
                      }}
                    >
                      <h3 class="text-xl font-extrabold text-white">
                        {t(`landing.howItWorks.${step}.title`)}
                      </h3>
                      <p class="mt-4 text-base leading-relaxed text-white/75">
                        {t(`landing.howItWorks.${step}.desc`)}
                      </p>
                    </article>
                    <Show when={i() < stepKeys.length - 1}>
                      <div class="hidden shrink-0 items-center justify-center px-3 lg:flex">
                        <span class="howitworks-flow-arrow-core">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.2"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M5 12h14m0 0-4-4m4 4-4 4"
                            />
                          </svg>
                        </span>
                      </div>
                    </Show>
                  </>
                )}
              </For>
            </div>
          </div>
        </section>

        {/* ═══════════════════ Product Categories ═══════════════════ */}
        <section
          id="explore-features"
          class="landing-band landing-band--warm relative z-10 py-20 sm:py-24"
        >
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="text-center">
              <h3 class="text-4xl font-extrabold text-white sm:text-5xl">
                {t('landing.explore.title')}
              </h3>
              <p class="mx-auto mt-4 max-w-2xl text-lg font-medium text-white/90">
                {t('landing.explore.subtitle')}
              </p>
            </div>

            <div class="mt-12 grid gap-6 md:grid-cols-2">
              <A
                href="/features/inventory-management"
                class="landing-card landing-card--interactive p-7"
              >
                <article>
                  <p class="text-xs font-semibold uppercase tracking-[0.12em] text-amber-100/90">
                    {t('landing.explore.category01')}
                  </p>
                  <h3 class="mt-3 text-2xl font-black text-white">
                    {t('landing.explore.inventory')}
                  </h3>
                  <p class="mt-4 text-sm leading-relaxed text-white/80">
                    {t('landing.explore.inventoryDesc')}
                  </p>
                </article>
              </A>

              <A
                href="/features/ai-analytics"
                class="landing-card landing-card--interactive p-7"
              >
                <article>
                  <p class="text-xs font-semibold uppercase tracking-[0.12em] text-amber-100/90">
                    {t('landing.explore.category02')}
                  </p>
                  <h3 class="mt-3 text-2xl font-black text-white">
                    {t('landing.explore.analytics')}
                  </h3>
                  <p class="mt-4 text-sm leading-relaxed text-white/80">
                    {t('landing.explore.analyticsDesc')}
                  </p>
                </article>
              </A>

              <A
                href="/features/barcode-mobile"
                class="landing-card landing-card--interactive p-7"
              >
                <article>
                  <p class="text-xs font-semibold uppercase tracking-[0.12em] text-amber-100/90">
                    {t('landing.explore.category03')}
                  </p>
                  <h3 class="mt-3 text-2xl font-black text-white">
                    {t('landing.explore.barcode')}
                  </h3>
                  <p class="mt-4 text-sm leading-relaxed text-white/80">
                    {t('landing.explore.barcodeDesc')}
                  </p>
                </article>
              </A>

              <A
                href="/features/team-permissions"
                class="landing-card landing-card--interactive p-7"
              >
                <article>
                  <p class="text-xs font-semibold uppercase tracking-[0.12em] text-amber-100/90">
                    {t('landing.explore.category04')}
                  </p>
                  <h3 class="mt-3 text-2xl font-black text-white">
                    {t('landing.explore.team')}
                  </h3>
                  <p class="mt-4 text-sm leading-relaxed text-white/80">
                    {t('landing.explore.teamDesc')}
                  </p>
                </article>
              </A>
            </div>
          </div>
        </section>

        {/* ═══════════════════ Story ═══════════════════ */}
        <section class="story-split relative z-10">
          {/* Left — full-bleed image */}
          <figure class="story-split-img-wrap">
            <img
              src="/story-hero.jpg"
              alt={t('landing.story.imgAlt')}
              class="story-split-img"
              width="881"
              height="1094"
            />
          </figure>

          {/* Right — prose */}
          <article class="story-split-text">
            <p class="story-eyebrow">{t('landing.story.eyebrow')}</p>
            <h3 class="story-title">{t('landing.story.title')}</h3>
            <p class="story-lead">{t('landing.story.lead')}</p>

            <div class="story-prose-points">
              <div class="story-prose-point">
                <p class="story-prose-heading">
                  {t('landing.story.p1heading')}
                </p>
                <p class="story-prose-body">{t('landing.story.p1body')}</p>
              </div>
              <div class="story-prose-point">
                <p class="story-prose-heading">
                  {t('landing.story.p2heading')}
                </p>
                <p class="story-prose-body">{t('landing.story.p2body')}</p>
              </div>
              <div class="story-prose-point">
                <p class="story-prose-heading">
                  {t('landing.story.p3heading')}
                </p>
                <p class="story-prose-body">{t('landing.story.p3body')}</p>
              </div>
            </div>

            <A
              href="/signup"
              onClick={() =>
                trackMarketingEvent('landing_cta_click', {
                  placement: 'story',
                  target: '/signup',
                })
              }
              class="story-cta"
            >
              {t('landing.story.cta')}
            </A>
          </article>
        </section>

        {/* ═══════════════════ Problem → Solution ═══════════════════ */}
        <section class="landing-band landing-band--clear relative z-10 py-20 sm:py-24">
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
              <div class="landing-card p-8 sm:p-10">
                <p class="text-xs font-semibold uppercase tracking-[0.14em] text-amber-100/90">
                  {t('landing.problem.eyebrow')}
                </p>
                <h2 class="mt-4 text-3xl font-extrabold text-white sm:text-4xl">
                  {t('landing.problem.title')}
                </h2>
                <p class="mt-5 text-lg font-medium leading-relaxed text-white/90">
                  {t('landing.problem.subtitle')}
                </p>
                <ul class="mt-8 space-y-4">
                  <For each={problemItems}>
                    {(item) => (
                      <li class="flex items-start gap-3 text-lg text-white/85">
                        <span class="mt-0.5 text-red-300">✗</span>
                        <span>{t(`landing.problem.${item}`)}</span>
                      </li>
                    )}
                  </For>
                </ul>
                <A
                  href="/signup"
                  onClick={() =>
                    trackMarketingEvent('landing_cta_click', {
                      placement: 'problem_solution',
                      target: '/signup',
                    })
                  }
                  class="mt-8 inline-block rounded-xl bg-white px-6 py-3 text-base font-bold text-amber-800 shadow-[0_12px_28px_rgba(120,53,15,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-50"
                >
                  {t('landing.problem.cta')}
                </A>
              </div>

              <div class="landing-card p-6 sm:p-8">
                <div class="grid gap-3 sm:grid-cols-2">
                  {/* ── Spreadsheet card ─────────────────────── */}
                  <div class="compare-spreadsheet">
                    {/* macOS-style titlebar */}
                    <div class="compare-ss-titlebar">
                      <span class="compare-ss-dot compare-ss-dot--red" />
                      <span class="compare-ss-dot compare-ss-dot--yellow" />
                      <span class="compare-ss-dot compare-ss-dot--green" />
                      <span class="compare-ss-filename">inventory_Q1.xlsx</span>
                    </div>
                    {/* Column headers */}
                    <div class="compare-ss-row compare-ss-col-headers">
                      <div class="compare-ss-row-num" />
                      <div class="compare-ss-col-hdr">
                        A — {t('landing.problem.spreadsheetLabel')}
                      </div>
                      <div class="compare-ss-col-hdr compare-ss-col-hdr--b">
                        B
                      </div>
                    </div>
                    {/* Data rows */}
                    <div class="compare-ss-row">
                      <div class="compare-ss-row-num">1</div>
                      <div class="compare-ss-cell compare-ss-cell--bad">
                        ⚠ {t('landing.problem.spreadsheetRow1')}
                      </div>
                      <div class="compare-ss-cell compare-ss-flag">✗</div>
                    </div>
                    <div class="compare-ss-row">
                      <div class="compare-ss-row-num">2</div>
                      <div class="compare-ss-cell compare-ss-cell--bad">
                        ⚠ {t('landing.problem.spreadsheetRow2')}
                      </div>
                      <div class="compare-ss-cell compare-ss-flag">✗</div>
                    </div>
                    <div class="compare-ss-row">
                      <div class="compare-ss-row-num">3</div>
                      <div class="compare-ss-cell compare-ss-cell--bad">
                        ⚠ {t('landing.problem.spreadsheetRow3')}
                      </div>
                      <div class="compare-ss-cell compare-ss-flag">✗</div>
                    </div>
                    {/* Impact footer */}
                    <div class="compare-ss-impact">
                      -4% {t('landing.problem.impactLabel')}
                    </div>
                  </div>

                  {/* ── khoai.io futuristic card ──────────────── */}
                  <div class="compare-khoai">
                    <div class="compare-khoai-header">
                      <p class="compare-khoai-title">khoai.io</p>
                      <div class="compare-khoai-badge">
                        <span class="compare-khoai-dot" />
                        LIVE
                      </div>
                    </div>
                    <div class="compare-khoai-rows">
                      <div class="compare-khoai-row">
                        <span class="compare-khoai-check">✓</span>
                        <span>{t('landing.problem.khaoioRow1')}</span>
                      </div>
                      <div class="compare-khoai-row">
                        <span class="compare-khoai-check">✓</span>
                        <span>{t('landing.problem.khaoioRow2')}</span>
                      </div>
                      <div class="compare-khoai-row">
                        <span class="compare-khoai-check">✓</span>
                        <span>{t('landing.problem.khaoioRow3')}</span>
                      </div>
                    </div>
                    <p class="compare-khoai-tagline">
                      {t('landing.problem.khaoioTagline')}
                    </p>
                  </div>
                </div>

                <div class="landing-card-sub mt-4 p-4">
                  <p class="text-sm font-bold text-white">
                    {t('landing.problem.comparisonTitle')}
                  </p>
                  <div class="mt-3 space-y-3 text-xs text-white/85">
                    <div>
                      <div class="mb-1 flex justify-between">
                        <span>{t('landing.problem.stockVisibility')}</span>
                        <span>92%</span>
                      </div>
                      <div class="landing-metric-track h-2 rounded-full">
                        <div class="landing-metric-fill landing-metric-fill--stock h-full w-[92%] rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div class="mb-1 flex justify-between">
                        <span>{t('landing.problem.alertResponseSpeed')}</span>
                        <span>81%</span>
                      </div>
                      <div class="landing-metric-track h-2 rounded-full">
                        <div class="landing-metric-fill landing-metric-fill--alert h-full w-[81%] rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div class="mb-1 flex justify-between">
                        <span>
                          {t('landing.problem.auditTraceCompleteness')}
                        </span>
                        <span>96%</span>
                      </div>
                      <div class="landing-metric-track h-2 rounded-full">
                        <div class="landing-metric-fill landing-metric-fill--audit h-full w-[96%] rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ Founder / Transparency ═══════════════════ */}
        <section class="landing-band landing-band--cool relative z-10 py-20 sm:py-24">
          <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div class="text-center">
              <h3 class="text-3xl font-extrabold text-white sm:text-4xl">
                {t('landing.founder.title')}
              </h3>
              <p class="mx-auto mt-4 max-w-2xl text-lg font-medium text-white/90">
                {t('landing.founder.subtitle')}
              </p>
            </div>
            <div class="mt-12 grid gap-8 sm:grid-cols-2">
              {/* Chat with founder */}
              <div class="landing-card landing-card--interactive p-8">
                <h3 class="text-xl font-extrabold text-white">
                  {t('landing.founder.chat')}
                </h3>
                <p class="mt-4 text-base leading-relaxed text-white/85">
                  {t('landing.founder.chatDesc')}
                </p>
                <a
                  href="mailto:human.khoai@gmail.com"
                  class="mt-7 inline-block rounded-lg border border-white/30 px-5 py-2.5 text-base font-bold text-white transition-colors hover:bg-white/10"
                >
                  Email the founder
                </a>
              </div>
              {/* Public roadmap */}
              <div class="landing-card landing-card--interactive p-8">
                <h3 class="text-xl font-extrabold text-white">
                  {t('landing.founder.social')}
                </h3>
                <p class="mt-4 text-base leading-relaxed text-white/85">
                  {t('landing.founder.socialDesc')}
                </p>
                <a
                  href="#"
                  class="mt-7 inline-block rounded-lg border border-white/30 px-5 py-2.5 text-base font-bold text-white/65 transition-colors hover:bg-white/10"
                  aria-disabled="true"
                  onClick={(e) => e.preventDefault()}
                >
                  {t('landing.founder.socialCta')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ FAQ ═══════════════════ */}
        <section class="landing-band landing-band--clear relative z-10 py-20 sm:py-24">
          <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div class="text-center">
              <h2 class="text-4xl font-extrabold text-white sm:text-5xl">
                {t('landing.faq.title')}
              </h2>
              <p class="mx-auto mt-4 max-w-xl text-lg font-medium text-white/90">
                {t('landing.faq.subtitle')}
              </p>
            </div>
            <div class="mt-12 space-y-4">
              <For each={faqKeys}>
                {(key) => (
                  <article class="landing-card p-7">
                    <h3 class="text-lg font-bold text-white">
                      {t(`landing.faq.q${key}`)}
                    </h3>
                    <p class="mt-3 text-base leading-relaxed text-white/85">
                      {t(`landing.faq.a${key}`)}
                    </p>
                  </article>
                )}
              </For>
            </div>
          </div>
        </section>

        {/* ═══════════════════ Final CTA ═══════════════════ */}
        <section class="landing-band landing-band--warm relative z-10 py-20 sm:py-24">
          <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <h2 class="text-4xl font-extrabold text-white sm:text-5xl">
              {t('landing.cta.title')}
            </h2>
            <p class="mx-auto mt-4 max-w-2xl text-lg font-medium leading-relaxed text-amber-50 sm:text-xl">
              {t('landing.cta.subtitle')}
            </p>
            <A
              href="/signup"
              class="mt-8 inline-block rounded-xl bg-white px-8 py-3.5 text-lg font-extrabold text-amber-800 shadow-[0_14px_30px_rgba(120,53,15,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-50"
            >
              {t('landing.cta.button')}
            </A>
          </div>
        </section>
      </main>

      {/* ═══════════════════ Footer ═══════════════════ */}
      <footer class="relative z-10 border-t border-slate-200 bg-white/70">
        <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div class="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p class="text-sm text-slate-600">{t('public.footer.copyright')}</p>
            <nav class="flex gap-6" aria-label="Footer">
              <A
                href="/privacy"
                class="text-sm text-slate-600 hover:text-slate-900"
              >
                {t('public.footer.privacy')}
              </A>
              <A
                href="/terms"
                class="text-sm text-slate-600 hover:text-slate-900"
              >
                {t('public.footer.terms')}
              </A>
              <A
                href="/billing-terms"
                class="text-sm text-slate-600 hover:text-slate-900"
              >
                {t('public.footer.billingTerms')}
              </A>
              <a
                href="mailto:human.khoai@gmail.com"
                class="text-sm text-slate-600 hover:text-slate-900"
              >
                {t('public.footer.contact')}
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
